import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format, isToday, isYesterday } from "date-fns";
import {
  ArrowLeft,
  Trash2,
  Coins,
  Receipt,
  Bell,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";
import { AdminPopup, ConfirmDialog, Empty, Pill, Skeleton } from "../../../dashboardAdmin/sidebar/adminLayout"  // adapte le chemin si besoin
import "./notification.css"; // même fichier que celui de l'admin (nouvelle version)

/* Catégories de l'admin commercial
   countKey = clé renvoyée par /unread-count-Comercial-by-type */
const CATEGORIES = [
  {
    key: "RECHARGE_RECEIPT_SUBMITTED",
    countKey: "POINT_RECHARGE",
    title: "Recharge requests",
    noun: "recharge",
    Icon: Coins,
    tone: "amber",
  },
  {
    key: "INVOICE_RECEIPT_SUBMITTED",
    countKey: "INVOICE",
    title: "Invoice receipts",
    noun: "invoice receipt",
    Icon: Receipt,
    tone: "blue",
  },
];

const getCategory = (key) => CATEGORIES.find((c) => c.key === key);

/* Origine des fichiers de reçus quand l'API renvoie un chemin relatif */
const FILES_ORIGIN = process.env.REACT_APP_FILES_URL || "https://mwgtd.com";

const resolveReceiptUrl = (receiptUrl) => {
  if (!receiptUrl) return null;
  return receiptUrl.startsWith("https") ? receiptUrl : `${FILES_ORIGIN}${receiptUrl}`;
};

/* Couleur du badge de statut */
const pillTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (["approved", "paid", "active", "verified"].includes(s)) return "ok";
  if (["rejected", "expired"].includes(s)) return "bad";
  return "warn";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  return format(date, "dd/MM/yyyy HH:mm");
};

const shortTime = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? "" : format(date, "HH:mm");
};

const dayLabel = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Earlier";
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "dd MMM yyyy");
};

const formatTnd = (millimes) => {
  const value = Number(millimes);
  return isNaN(value) ? "N/A" : `${(value / 1000).toFixed(3)} TND`;
};

/* ── Petits composants d'affichage ── */
const InfoRow = ({ label, value, strong }) => (
  <div className="ntf-row">
    <span className="ntf-row-label">{label}</span>
    <span className="ntf-row-value">
      {value || value === 0 ? (strong ? <strong>{value}</strong> : value) : "N/A"}
    </span>
  </div>
);

const DetailHead = ({ title, subtitle, status }) => (
  <div className="ntf-detail-head">
    <div>
      <h4>{title}</h4>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {status && <Pill tone={pillTone(status)}>{status}</Pill>}
  </div>
);

const ReceiptCard = ({ url }) => {
  const isPdf = url.toLowerCase().includes(".pdf");
  return (
    <div className="ntf-receipt">
      <div className="ntf-receipt-head row">
        <span>Receipt</span>
        <a className="ntf-receipt-open" href={url} target="_blank" rel="noreferrer">
          <ExternalLink size={13} /> Open
        </a>
      </div>
      {isPdf ? (
        <iframe src={url} title="Receipt" className="ntf-receipt-frame" />
      ) : (
        <a href={url} target="_blank" rel="noreferrer" title="Open full size">
          <img src={url} alt="Receipt" className="ntf-receipt-img" />
        </a>
      )}
    </div>
  );
};

/* Confirmation avec motif de refus (remplace prompt()) */
const ReasonDialog = ({ open, title, message, confirmLabel, reason, setReason, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="adm-modal-overlay" onMouseDown={onCancel}>
      <div
        className="adm-confirm"
        role="alertdialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="adm-confirm-icon danger">
          <AlertTriangle size={18} />
        </div>
        <h4>{title}</h4>
        <p>{message}</p>
        <textarea
          className="ntf-reason"
          rows={3}
          maxLength={300}
          autoFocus
          placeholder="Reason (optional) — shown to the client"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="adm-confirm-actions">
          <button className="adm-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="adm-btn danger-solid" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Notifications = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [viewMode, setViewMode] = useState("types");
  const [listFilter, setListFilter] = useState("all");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true); // premier chargement uniquement
  const [busy, setBusy] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const authCfg = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  /* Un ToastContainer n'est ajouté ici que si l'application n'en a pas déjà un */
  const [ownToasts] = useState(
    () => !document.querySelector(".Toastify, .Toastify__toast-container")
  );

  /* ── Chargement : notifications + compteurs non lus (endpoint commercial) ── */
  const loadData = async (silent = false) => {
    if (!localStorage.getItem("token")) {
      setLoading(false);
      return;
    }
    try {
      const [notifsRes, countsRes] = await Promise.all([
        axios.get(`${API_URL}/notifications`, authCfg()),
        axios.get(`${API_URL}/unread-count-Comercial-by-type`, authCfg()),
      ]);
      const notifs = Array.isArray(notifsRes.data)
        ? notifsRes.data
        : notifsRes.data.notifications || [];
      setNotifications(notifs);
      setUnreadCounts(countsRes.data || {});
    } catch (err) {
      console.error(err);
      if (!silent) toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(true), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Marquer comme lue : mise à jour immédiate, puis appel serveur */
  const markAsRead = async (notif) => {
    if (notif.read) return;

    setNotifications((prev) => prev.map((n) => (n._id === notif._id ? { ...n, read: true } : n)));
    const cat = CATEGORIES.find((c) => c.key === notif.type);
    if (cat) {
      setUnreadCounts((prev) => ({
        ...prev,
        [cat.countKey]: Math.max(0, (prev[cat.countKey] || 0) - 1),
      }));
    }

    try {
      await axios.post(`${API_URL}/notifications/${notif._id}/read`, {}, authCfg());
    } catch (err) {
      console.error("Erreur mark as read", err);
      loadData(true);
    }
  };

  /* ── Navigation ── */
  const resetDetails = () => {
    setSelectedNotification(null);
    setUserDetails(null);
    setReceiptUrl(null);
  };

  const togglePopup = (type = null, mode = "types") => {
    setSelectedType(type);
    setViewMode(mode);
    setConfirm(null);
    if (mode === "types" || mode === "list") {
      resetDetails();
      setListFilter("all");
    }
  };

  const closePopup = () => {
    onClose?.();
    setViewMode("types");
    setSelectedType(null);
    setConfirm(null);
    resetDetails();
  };

  /* Retour d'un niveau : détails → liste → catégories → fermeture */
  const handleBack = () => {
    if (viewMode === "details") {
      resetDetails();
      setViewMode("list");
    } else if (viewMode === "list") {
      togglePopup(null, "types");
    } else {
      closePopup();
    }
  };

  /* Touche Échap : ferme d'abord la confirmation, puis remonte d'un niveau */
  const handleEscape = () => {
    if (confirm) return setConfirm(null);
    handleBack();
  };

  /* ── Actions admin commercial ── */
  const finish = async (message) => {
    toast.success(message);
    togglePopup(selectedType, "list");
    await loadData(true);
  };

  const fail = (err) => toast.error(err.response?.data?.message || "Failed");

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const decideRecharge = async (requestId, status, adminReason) => {
    if (!requestId) return toast.error("Missing ID");
    try {
      const body =
        status === "approved"
          ? { status }
          : { status, adminMessage: adminReason || "No reason provided" };
      await axios.put(`${API_URL}/point-recharge/${requestId}/status`, body, authCfg());
      await finish(status === "approved" ? "Recharge approved!" : "Recharge rejected");
    } catch (err) {
      fail(err);
    }
  };

  const decideInvoice = async (invoiceId, action, adminReason) => {
    if (!invoiceId) return toast.error("Missing invoice ID");
    try {
      const body = action === "approve" ? {} : { adminMessage: adminReason || "No reason provided" };
      await axios.put(`${API_URL}/invoices/receipt/${invoiceId}/${action}`, body, authCfg());
      await finish(action === "approve" ? "Payment accepted!" : "Payment rejected");
    } catch (err) {
      fail(err);
    }
  };

  const removeNotification = async (notifId) => {
    try {
      await axios.delete(`${API_URL}/notifications/${notifId}`, authCfg());
      toast.success("Deleted");
      await loadData(true);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const fetchUserDetails = async (userId) => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API_URL}/users/${userId}`, authCfg());
      setUserDetails(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  /* Clic sur une notification de la liste */
  const handleNotificationClick = (notif) => {
    markAsRead(notif);
    resetDetails();
    setSelectedNotification(notif);
    setReceiptUrl(resolveReceiptUrl(notif.receiptUrl));
    fetchUserDetails(notif.user);
    setViewMode("details");
  };

  /* ── Confirmations ── */
  const ask = (options) => {
    setReason("");
    setConfirm(options);
  };

  const runConfirm = async () => {
    const current = confirm;
    const typedReason = reason.trim();
    setConfirm(null);
    setReason("");
    if (current?.onConfirm) await current.onConfirm(typedReason);
  };

  const askDelete = (notif) =>
    ask({
      title: "Delete this notification?",
      message: "It will be permanently removed from the list.",
      confirmLabel: "Delete",
      danger: true,
      onConfirm: () => removeNotification(notif._id),
    });

  /* ── Boutons d'action du pied de popup ── */
  const getActions = () => {
    if (viewMode !== "details" || !selectedNotification) return null;
    if (selectedNotification.status !== "pending") return null;

    const n = selectedNotification;
    const isRecharge = selectedType === "RECHARGE_RECEIPT_SUBMITTED";
    const rechargeId = n.requestId || n._id;
    const invoiceId = n.invoice?._id || n.invoice;

    return [
      {
        label: "Reject",
        kind: "danger",
        onClick: () =>
          ask({
            title: isRecharge ? "Reject this recharge request?" : "Reject this payment receipt?",
            message: "The client will be notified.",
            confirmLabel: "Reject",
            danger: true,
            withReason: true,
            onConfirm: (r) =>
              run(() =>
                isRecharge
                  ? decideRecharge(rechargeId, "rejected", r)
                  : decideInvoice(invoiceId, "reject", r)
              ),
          }),
      },
      {
        label: "Accept",
        kind: "primary",
        onClick: () =>
          ask({
            title: isRecharge ? "Approve this recharge request?" : "Approve this payment receipt?",
            message: isRecharge
              ? "The points will be credited to the client."
              : "The invoice will be marked as paid.",
            confirmLabel: "Approve",
            danger: false,
            onConfirm: () =>
              run(() =>
                isRecharge
                  ? decideRecharge(rechargeId, "approved")
                  : decideInvoice(invoiceId, "approve")
              ),
          }),
      },
    ];
  };

  /* ── Valeurs dérivées ── */
  const getCardSummary = (cat) => {
    const items = notifications.filter((n) => n.type === cat.key);
    const unread = unreadCounts[cat.countKey] || 0;
    let subtitle = "All caught up";
    if (unread > 0) subtitle = `${unread} to review`;
    else if (items.length === 0) subtitle = "No notifications";
    return { total: items.length, unread, subtitle };
  };

  const totalUnread = CATEGORIES.reduce((sum, c) => sum + (unreadCounts[c.countKey] || 0), 0);
  const currentCategory = getCategory(selectedType);

  const categoryNotifications = notifications
    .filter((n) => n.type === selectedType)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredNotifications =
    listFilter === "unread" ? categoryNotifications.filter((n) => !n.read) : categoryNotifications;

  const groups = [];
  filteredNotifications.forEach((n) => {
    const label = dayLabel(n.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(n);
    else groups.push({ label, items: [n] });
  });

  const actions = getActions();
  const n = selectedNotification;

  const headTitle = viewMode === "types" || !currentCategory ? "Notifications" : currentCategory.title;
  const headCount =
    viewMode === "types"
      ? totalUnread
      : currentCategory
      ? unreadCounts[currentCategory.countKey] || 0
      : 0;

  // === RENDER ===
  return (
    <AdminPopup onClose={closePopup} onEscape={handleEscape}>
      <div className="ntf-shell">
        <div className="adm-topbar">
          <button className="adm-back" onClick={handleBack} aria-label="Back">
            <ArrowLeft /> Back
          </button>
          <h3>{headTitle}</h3>
          {headCount > 0 && viewMode !== "details" && (
            <span className="adm-topbar-count">{headCount} unread</span>
          )}
        </div>

        <div className="adm-body">
          {/* === Accueil : cartes par catégorie === */}
          {viewMode === "types" &&
            (loading ? (
              <Skeleton rows={3} />
            ) : (
              <>
                <div className="ntf-home-head">
                  <span className="ntf-bell">
                    <Bell size={18} />
                  </span>
                  <div>
                    <h2>Notifications</h2>
                    <p>{totalUnread > 0 ? `${totalUnread} to review` : "You're all caught up"}</p>
                  </div>
                </div>
                <div className="ntf-cards">
                  {CATEGORIES.map((cat) => {
                    const { total, unread, subtitle } = getCardSummary(cat);
                    const { key, title, Icon, tone } = cat;
                    return (
                      <button
                        key={key}
                        type="button"
                        className="ntf-card"
                        onClick={() => togglePopup(key, "list")}
                      >
                        <span className={`ntf-icon lg ${tone}`}>
                          <Icon size={18} />
                        </span>
                        <span className="ntf-card-text">
                          <span className="ntf-card-title">{title}</span>
                          <span className={`ntf-card-sub ${unread > 0 ? "alert" : ""}`}>{subtitle}</span>
                          <span className="ntf-card-meta">{total} total</span>
                        </span>
                        {unread > 0 && <span className="ntf-badge">{unread}</span>}
                        <ChevronRight className="ntf-card-arrow" />
                      </button>
                    );
                  })}
                </div>
              </>
            ))}

          {/* === Liste des notifications === */}
          {viewMode === "list" && selectedType && currentCategory && (
            <>
              <div className="adm-filters ntf-filters">
                {[
                  ["all", "All", categoryNotifications.length],
                  ["unread", "Unread", unreadCounts[currentCategory.countKey] || 0],
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    className={`adm-chip ${listFilter === key ? "active" : ""}`}
                    onClick={() => setListFilter(key)}
                  >
                    {label}
                    <span className="adm-chip-count">{count}</span>
                  </button>
                ))}
              </div>

              {groups.length === 0 ? (
                <Empty>
                  <Bell size={20} />
                  {listFilter === "unread"
                    ? `No unread ${currentCategory.noun} notifications`
                    : `No ${currentCategory.noun} notifications`}
                </Empty>
              ) : (
                groups.map((group) => (
                  <div key={group.label} className="ntf-group">
                    <div className="ntf-day">{group.label}</div>
                    <ul className="ntf-list">
                      {group.items.map((notif) => {
                        const CatIcon = currentCategory.Icon;
                        const isRecharge = selectedType === "RECHARGE_RECEIPT_SUBMITTED";
                        return (
                          <li
                            key={notif._id}
                            className={`ntf-item ${!notif.read ? "unread" : ""}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleNotificationClick(notif)}
                            onKeyDown={(e) => e.key === "Enter" && handleNotificationClick(notif)}
                          >
                            <span className={`ntf-icon ${currentCategory.tone}`}>
                              <CatIcon size={16} />
                            </span>
                            <div className="ntf-item-main">
                              <p className="ntf-item-msg">
                                <strong>
                                  {notif.clientName} {notif.clientSurname}
                                </strong>
                                {isRecharge ? (
                                  <>
                                    {" "}
                                    requested <strong>{notif.points} points</strong> ({formatTnd(notif.totalPrice)})
                                  </>
                                ) : (
                                  <> submitted an invoice receipt</>
                                )}
                              </p>
                              <span className="ntf-item-meta">
                                {notif.status && (
                                  <span className={`ntf-tag ${notif.status}`}>{notif.status}</span>
                                )}
                                <span title={formatDate(notif.createdAt)}>{shortTime(notif.createdAt)}</span>
                              </span>
                            </div>
                            {!notif.read && <span className="ntf-dot" aria-label="Unread" />}
                            <button
                              className="adm-icon-btn sm danger"
                              title="Delete"
                              aria-label="Delete notification"
                              onClick={(e) => {
                                e.stopPropagation();
                                askDelete(notif);
                              }}
                            >
                              <Trash2 />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))
              )}
            </>
          )}

          {/* === Détails === */}
          {viewMode === "details" && n && (
            <div className="ntf-details">
              <DetailHead
                title={selectedType === "RECHARGE_RECEIPT_SUBMITTED" ? "Recharge request" : "Invoice receipt"}
                subtitle={formatDate(n.createdAt)}
                status={n.status || "pending"}
              />

              <div className="ntf-info">
                <InfoRow label="Name" value={n.clientName || userDetails?.name} />
                <InfoRow label="Surname" value={n.clientSurname || userDetails?.surname} />
                <InfoRow label="Email" value={n.clientEmail || userDetails?.email} />
                {selectedType === "RECHARGE_RECEIPT_SUBMITTED" && (
                  <>
                    <InfoRow label="Points" value={n.points} strong />
                    <InfoRow label="Total" value={formatTnd(n.totalPrice)} strong />
                  </>
                )}
                <InfoRow label="Submitted" value={formatDate(n.createdAt)} />
              </div>

              {receiptUrl ? <ReceiptCard url={receiptUrl} /> : <Empty>No receipt attached</Empty>}
            </div>
          )}
        </div>

        {/* Actions toujours visibles en bas du popup */}
        {actions && (
          <div className="ntf-footer">
            {actions.map((a) => (
              <button key={a.label} className={`adm-btn ${a.kind}`} onClick={a.onClick} disabled={busy}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {confirm?.withReason ? (
        <ReasonDialog
          open
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          reason={reason}
          setReason={setReason}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      ) : (
        <ConfirmDialog
          open={Boolean(confirm)}
          title={confirm?.title}
          message={confirm?.message}
          confirmLabel={confirm?.confirmLabel}
          danger={confirm?.danger}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {ownToasts && <ToastContainer position="top-right" autoClose={3000} theme="dark" />}
    </AdminPopup>
  );
};

export default Notifications;